/**
 * The Simulation: the entire game, minus presentation.
 *
 * No renderer, no camera, no HUD, no audio, no input devices. Those live in
 * game.ts, which wraps this. The split exists so tests/sim/ can drive the REAL
 * cat, the REAL granny and the REAL physics headlessly and deterministically.
 * If the sim tests exercised a reimplementation of this logic they would be
 * worthless - they would pass while the shipped game broke.
 *
 * READ `step()` FIRST. The order of operations there is the architecture, and
 * several subtle behaviors depend on it - most importantly that the cat's noise
 * is emitted BEFORE granny's perception runs, so she reacts on the same step
 * rather than one step late.
 *
 * Presentation requests leave through the event bus ('vfx:pop', 'camera:shake').
 * This module must never import from src/render or src/ui.
 */

import * as THREE from 'three';
import { EventBus } from '@/core/events';
import { Rng } from '@/core/rng';
import { tuning } from '@/core/tuning';
import { clone, distance, distanceXZ, vec3, type Vec3 } from '@/core/math';
import { createEmptyFrame, type InputFrame } from '@/engine/input';
import { PhysicsWorld } from '@/engine/physicsWorld';
import { PropSystem, type PropInstance } from '@/world/prop';
import { CatController } from '@/cat/catController';
import type { RagdollReason } from '@/cat/ragdoll';
import { Granny, type CatSnapshot } from '@/ai/granny';
import { isInsideVolume } from '@/ai/sensors';
import {
  addHeat,
  createHeatState,
  resetHeatAfterCatch,
  stepHeat,
  type HeatState,
} from '@/systems/heat';
import {
  addTrace,
  clearTraces,
  createTraceStore,
  hasUnresolvedTraceInRoom,
  stepTraces,
  type TraceStore,
} from '@/systems/traces';
import { growthForTrace, resolveMischief } from '@/systems/mischief';
import { scoreLevel, type LevelResult, type ScoredResult } from '@/systems/score';
import { loadLevel, type KeyInstance, type LoadedLevel } from '@/levels/loader';
import type { ObjectiveDef } from '@/levels/schema';

export interface ObjectiveProgress {
  def: ObjectiveDef;
  current: number;
  complete: boolean;
}

export type GamePhase = 'playing' | 'caught' | 'won';

/** Movement basis, supplied by the camera. Identity means "world axes". */
export interface MovementBasis {
  forward: Vec3;
  right: Vec3;
}

export const WORLD_BASIS: MovementBasis = {
  forward: { x: 0, y: 0, z: -1 },
  right: { x: 1, y: 0, z: 0 },
};

/** How long the cat must dwell on a Cat Moment for it to count. */
const MOMENT_DWELL_SECONDS = 1.2;

export class Simulation {
  readonly bus: EventBus;
  readonly rng: Rng;
  readonly physics: PhysicsWorld;
  readonly props: PropSystem;
  readonly heat: HeatState = createHeatState();
  readonly traces: TraceStore = createTraceStore();
  readonly level: LoadedLevel;
  readonly cat: CatController;
  readonly granny: Granny;
  readonly objectives: ObjectiveProgress[];

  phase: GamePhase = 'playing';
  chaosScore = 0;
  cozyPoints = 0;
  keysCollected = 0;
  detections = 0;
  /** Simulated seconds since the level loaded. Fixed-step, so it is exact. */
  elapsedSeconds = 0;

  private catchTimer = 0;
  private finalScore: ScoredResult | null = null;
  private hideSpotId: string | null = null;
  private hideEntryWitnessed = false;
  private witnessedKnocks = new Set<string>();
  private carriedProp: PropInstance | null = null;
  private momentDwell = new Map<string, number>();

  constructor(options: { scene: THREE.Scene; levelData: unknown; bus?: EventBus }) {
    this.bus = options.bus ?? new EventBus();
    this.physics = new PhysicsWorld();

    this.props = new PropSystem(options.scene, this.physics, {
      onKnocked: (prop) => this.onPropKnocked(prop),
      onSettled: (prop, broke) => this.onPropSettled(prop, broke),
      onBroken: (prop) => this.onPropBroken(prop),
    });

    this.level = loadLevel(options.levelData, {
      scene: options.scene,
      physics: this.physics,
      props: this.props,
    });
    this.rng = new Rng(this.level.def.seed);

    const bed = this.level.def.catBed;
    this.cat = new CatController(this.physics, vec3(bed[0], bed[1], bed[2]), options.scene);

    const spawn = this.level.def.granny.spawn;
    this.granny = new Granny(
      {
        physics: this.physics,
        scene: options.scene,
        heat: this.heat,
        traces: this.traces,
        bus: this.bus,
        rng: this.rng,
        hideSpots: this.level.hideSpots,
        getCat: () => this.catSnapshot(),
        roomIdAt: this.level.roomIdAt,
        catOnlyVolumes: this.level.catOnlyVolumes,
      },
      vec3(spawn[0], spawn[1], spawn[2]),
      this.level.def.granny.routine,
    );

    this.objectives = this.level.def.objectives.map((def) => ({
      def,
      current: 0,
      complete: false,
    }));
    this.wireEvents();
  }

  /* ------------------------------------------------------------------ */
  /* THE FIXED STEP - read this to understand the whole game            */
  /* ------------------------------------------------------------------ */

  step(
    dt: number,
    input: InputFrame = createEmptyFrame(),
    basis: MovementBasis = WORLD_BASIS,
  ): void {
    if (this.phase === 'caught') {
      this.stepCatchSequence(dt);
      return;
    }
    if (this.phase === 'won') return;
    this.elapsedSeconds += dt;

    // 1. The cat.
    this.cat.step(dt, input, basis);
    if (this.cat.landedHardThisStep) {
      const ragdoll = tuning.cat.ragdoll;
      this.startCatRagdoll(
        'fall',
        vec3(
          this.cat.velocity.x * ragdoll.fallImpulseHorizontalScale,
          this.cat.landingImpactSpeed * ragdoll.fallImpulseVerticalScale,
          this.cat.velocity.z * ragdoll.fallImpulseHorizontalScale,
        ),
      );
      this.bus.emit('camera:shake', { amount: ragdoll.cameraShake.fall });
    }

    // 2. Emit the cat's noise BEFORE perception runs, so granny hears it on this
    //    same step. Moving this below granny.step() introduces a one-frame lag
    //    that players feel as unresponsive AI.
    if (this.cat.noise.loudness > 0) {
      this.bus.emit('noise', {
        position: clone(this.cat.position),
        loudness: this.cat.noise.loudness,
        source: 'cat',
        tag: this.cat.noise.tag,
      });
    }

    // 3. Physics, then read the results back into props.
    this.physics.step();
    this.cat.syncAfterPhysicsStep();
    this.props.step(dt);

    // 4. Hide-spot occupancy, which perception depends on.
    this.updateHideSpot();

    // 5. Carrying, interactions, collectibles.
    if (!this.cat.isRagdollActive) this.updateCarry(input.justPressed.has('interact'));
    this.updateKeys();
    this.updateCatMoments(dt);

    // 6. Perception and behavior. Contact is stepped inside granny.step().
    this.granny.step(dt);

    // 7. Heat decay and tier transitions, using the world state as it now is.
    stepTraces(this.traces, dt);
    const result = stepHeat(
      this.heat,
      {
        observed: this.granny.canSeeCat,
        hidden: this.hideSpotId !== null,
        quiet: this.cat.noise.loudness < tuning.granny.hearing.threshold,
        unresolvedTraceInGrannyRoom: hasUnresolvedTraceInRoom(this.traces, this.granny.roomId),
      },
      dt,
    );
    if (result.tierChange) {
      this.bus.emit('heat:tier', { ...result.tierChange, heat: this.heat.heat });
    }

    // 8. Win check.
    this.checkWin();
  }

  /* ------------------------------------------------------------------ */
  /* Prop / mischief handling                                           */
  /* ------------------------------------------------------------------ */

  private onPropKnocked(prop: PropInstance): void {
    // Record whether she saw it start moving. A crash she only heard the end of
    // still counts as witnessed if she saw the initial shove.
    if (this.granny.canSeeCat) this.witnessedKnocks.add(prop.id);

    this.bus.emit('prop:toppled', { propId: prop.id, position: clone(prop.object.position) });
    this.bus.emit('noise', {
      position: clone(prop.object.position),
      loudness: 12,
      source: 'prop',
      tag: 'wobble',
    });
    this.countMischiefObjective(prop);
  }

  /** Where mischief is actually resolved - see the note on PropEvents.onSettled. */
  private onPropSettled(prop: PropInstance, broke: boolean): void {
    if (!prop.mischief) return;

    const witnessed = this.granny.canSeeCat || this.witnessedKnocks.has(prop.id);
    const position = clone(prop.object.position);
    const resolution = resolveMischief(prop.mischief, position, witnessed);
    if (!resolution) return;

    addHeat(this.heat, resolution.heat);
    this.chaosScore += resolution.chaos;

    this.bus.emit('mischief', { actionId: prop.mischief, position, propId: prop.id, witnessed });
    this.bus.emit('heat:changed', {
      heat: this.heat.heat,
      delta: resolution.heat,
      reason: prop.mischief,
    });
    this.bus.emit('noise', {
      position,
      loudness: resolution.noise.loudness,
      source: 'prop',
      tag: resolution.noise.tag,
    });

    if (resolution.trace) {
      const roomId = this.level.roomIdAt(position.x, position.z) ?? 'unknown';
      const growth = growthForTrace(resolution.trace.kind);
      const trace = addTrace(this.traces, {
        kind: resolution.trace.kind,
        position,
        roomId,
        heatOnFound: resolution.trace.heatOnFound,
        ...(growth ? { growth } : {}),
      });
      this.bus.emit('trace:created', { traceId: trace.id, kind: trace.kind, position, roomId });
    }

    // A witnessed crash gets a louder pop, because the player needs to know
    // instantly that they were seen.
    this.bus.emit('vfx:pop', {
      position,
      text: broke ? 'CRASH!' : 'THUD!',
      color: witnessed ? '#ff6b35' : '#ffd23f',
    });
    this.bus.emit('vfx:dust', { position, amount: broke ? 10 : 5 });
    this.bus.emit('camera:shake', { amount: broke ? 0.9 : 0.4 });
  }

  private onPropBroken(prop: PropInstance): void {
    this.bus.emit('prop:broken', {
      propId: prop.id,
      position: clone(prop.object.position),
      material: 'ceramic',
    });

    // Mischief keys: revealed only by destroying the right thing. This is what
    // forces engagement with the core verb rather than pure exploration.
    for (const key of this.level.keys) {
      if (key.revealedBy !== prop.id || key.available) continue;
      key.available = true;
      if (key.object) key.object.visible = true;
      this.bus.emit('vfx:pop', {
        position: clone(prop.object.position),
        text: 'A KEY!',
        color: '#f2c14e',
      });
    }
  }

  private countMischiefObjective(prop: PropInstance): void {
    for (const objective of this.objectives) {
      if (objective.complete || objective.def.kind !== 'mischief') continue;
      const matches = objective.def.matchActions;
      if (matches && (!prop.mischief || !matches.includes(prop.mischief))) continue;
      this.advanceObjective(objective);
    }
  }

  private advanceObjective(objective: ObjectiveProgress): void {
    objective.current = Math.min(objective.current + 1, objective.def.target);
    this.bus.emit('objective:progress', {
      objectiveId: objective.def.id,
      current: objective.current,
      target: objective.def.target,
    });
    if (objective.current >= objective.def.target && !objective.complete) {
      objective.complete = true;
      this.bus.emit('objective:complete', { objectiveId: objective.def.id });
    }
  }

  /* ------------------------------------------------------------------ */
  /* Cat state queries                                                  */
  /* ------------------------------------------------------------------ */

  private catSnapshot(): CatSnapshot {
    return {
      position: this.cat.position,
      headPosition: this.cat.headPosition,
      height: tuning.cat.height,
      crouching: this.cat.crouching,
      moving: this.cat.speed > 0.2,
      hideSpotId: this.hideSpotId,
      // Concealed only if she did not watch you get in. If she did, the hide
      // spot is the first place she looks.
      concealed: this.hideSpotId !== null && !this.hideEntryWitnessed,
    };
  }

  private updateHideSpot(): void {
    const previous = this.hideSpotId;
    let found: string | null = null;

    for (const spot of this.level.hideSpots) {
      if (isInsideVolume(this.cat.position, spot.position, spot.size)) {
        found = spot.id;
        break;
      }
    }

    this.hideSpotId = found;
    if (found && found !== previous) {
      // Snapshot at the moment of entry - this is the whole risk of diving into
      // a box while she is looking at you.
      this.hideEntryWitnessed = this.granny.canSeeCat;
      this.bus.emit('hideSpot:entered', { spotId: found, witnessed: this.hideEntryWitnessed });
    } else if (!found) {
      this.hideEntryWitnessed = false;
    }
    if (previous && previous !== found) this.bus.emit('hideSpot:left', { spotId: previous });
  }

  /** Whether granny watched the cat enter its current spot. Presentation reads this. */
  get hideEntrySeen(): boolean {
    return this.hideSpotId !== null && this.hideEntryWitnessed;
  }

  get hidingIn(): string | null {
    return this.hideSpotId;
  }

  /**
   * Midpoint of the level's room rectangles. Used to aim the camera inward on
   * load, and a sensible fallback target for anything that needs "the middle".
   */
  get roomCenter(): Vec3 {
    const rooms = this.level.def.rooms;
    let x = 0;
    let z = 0;
    for (const room of rooms) {
      x += (room.min[0] + room.max[0]) / 2;
      z += (room.min[1] + room.max[1]) / 2;
    }
    return vec3(x / rooms.length, 0, z / rooms.length);
  }

  get carrying(): PropInstance | null {
    return this.carriedProp;
  }

  /* ------------------------------------------------------------------ */
  /* Carrying, keys, moments                                            */
  /* ------------------------------------------------------------------ */

  private updateCarry(interactPressed: boolean): void {
    if (!interactPressed) return;

    if (this.carriedProp) {
      const dropAt = clone(this.cat.position);
      const prop = this.carriedProp;
      this.props.drop(prop, dropAt);
      this.carriedProp = null;
      this.checkHeistObjectives(prop, dropAt);
      return;
    }

    const nearby = this.props.nearest(this.cat.position, 0.55, (p) => p.carryable);
    if (nearby && this.props.pickUp(nearby)) {
      this.carriedProp = nearby;
      this.bus.emit('vfx:pop', {
        position: clone(this.cat.position),
        text: 'GOT IT!',
        color: '#9ee7ff',
      });
    }
  }

  private checkHeistObjectives(prop: PropInstance, dropAt: Vec3): void {
    for (const objective of this.objectives) {
      if (objective.complete || objective.def.kind !== 'heist') continue;
      if (objective.def.carryPropId !== prop.id) continue;
      const zone = objective.def.dropZone;
      if (!zone) continue;
      const target = vec3(zone.position[0], zone.position[1], zone.position[2]);
      if (distanceXZ(dropAt, target) <= zone.radius) this.advanceObjective(objective);
    }
  }

  private updateKeys(): void {
    for (const key of this.level.keys) {
      if (key.collected) continue;

      // The granny key rides in her apron. It becomes snatchable only while she
      // is doubled over wheezing - a real, funny, high-risk window rather than a
      // pickup you stumble into.
      if (key.flavor === 'granny') {
        if (!this.granny.exhausted) continue;
        if (distanceXZ(this.cat.position, this.granny.position) > 1.2) continue;
        this.collectKey(key, clone(this.granny.position));
        continue;
      }

      if (!key.available || !key.position) continue;
      if (distance(this.cat.position, key.position) > 0.45) continue;
      this.collectKey(key, key.position);
    }
  }

  private collectKey(key: KeyInstance, at: Vec3): void {
    key.collected = true;
    this.keysCollected++;
    if (key.object) key.object.visible = false;
    this.bus.emit('key:collected', { keyId: key.id, flavor: key.flavor });
    this.bus.emit('vfx:pop', { position: clone(at), text: 'KEY!', color: '#f2c14e' });
  }

  private updateCatMoments(dt: number): void {
    // Cat Moments are the "just be a cat" track: zero stakes, no fail, and they
    // unlock half the abilities. A kid who finds stealth stressful progresses
    // this way instead. See DESIGN.md 1.6.
    for (const moment of this.level.catMoments) {
      if (moment.completed) continue;

      const near = distance(this.cat.position, moment.position) < 0.5;
      const still = this.cat.speed < 0.4;

      if (!near || !still) {
        this.momentDwell.set(moment.id, 0);
        continue;
      }

      const dwell = (this.momentDwell.get(moment.id) ?? 0) + dt;
      this.momentDwell.set(moment.id, dwell);
      if (dwell < MOMENT_DWELL_SECONDS) continue;

      moment.completed = true;
      this.level.setCatMomentCompleted(moment.id);
      this.cozyPoints += moment.cozyPoints;
      this.bus.emit('catMoment:completed', {
        momentId: moment.id,
        cozyPoints: moment.cozyPoints,
      });
      this.bus.emit('vfx:pop', { position: moment.position, text: 'COZY', color: '#9ee7ff' });

      for (const objective of this.objectives) {
        if (objective.def.kind === 'discovery') this.advanceObjective(objective);
      }
    }
  }

  /* ------------------------------------------------------------------ */
  /* Catch / win                                                        */
  /* ------------------------------------------------------------------ */

  private wireEvents(): void {
    this.bus.on('cat:sprayed', ({ position }) => {
      const ragdoll = tuning.cat.ragdoll;
      const away = vec3(
        this.cat.position.x - this.granny.position.x,
        0,
        this.cat.position.z - this.granny.position.z,
      );
      const length = Math.hypot(away.x, away.z);
      const horizontalScale = length > Number.EPSILON ? ragdoll.sprayImpulseHorizontal / length : 0;
      this.startCatRagdoll(
        'spray',
        vec3(away.x * horizontalScale, ragdoll.sprayImpulseUp, away.z * horizontalScale),
      );
      this.bus.emit('vfx:pop', { position, text: 'SPLOOSH!', color: '#7fd4e8' });
      this.bus.emit('camera:shake', { amount: ragdoll.cameraShake.spray });
    });

    this.bus.on('cat:caught', ({ position }) => {
      if (this.phase !== 'playing') return;
      this.phase = 'caught';
      this.catchTimer = 0;
      this.cat.lock();
      const bed = this.level.def.catBed;
      this.granny.beginCarry(vec3(bed[0], bed[1], bed[2]));
      this.bus.emit('vfx:pop', { position, text: 'GOTCHA!', color: '#e03131' });
      this.bus.emit('level:failed', { levelId: this.level.def.id, reason: 'caught' });
    });

    // Cozy Points for being a delightful cat, awarded by the person chasing you
    // with a spray bottle twenty seconds later. See DESIGN.md 6.
    this.bus.on('granny:petted', ({ position, cozyPoints }) => {
      this.cozyPoints += cozyPoints;
      this.bus.emit('vfx:pop', { position, text: 'PURR', color: '#9ee7ff' });
    });

    this.bus.on('cat:spotted', () => {
      this.detections++;
      this.bus.emit('vfx:pop', {
        position: clone(this.granny.position),
        text: '!',
        color: '#e03131',
      });
    });

    this.bus.on('heat:tier', ({ to }) => {
      if (to === 'suspicious') {
        this.bus.emit('vfx:pop', {
          position: clone(this.granny.position),
          text: '?',
          color: '#ffa94d',
        });
      }
      if (to === 'frenzy') {
        this.bus.emit('vfx:pop', {
          position: clone(this.granny.position),
          text: 'RRRAAA!',
          color: '#e03131',
        });
      }
    });
  }

  private startCatRagdoll(reason: RagdollReason, impulse: Vec3): void {
    this.cat.startRagdoll(reason, impulse);
    this.bus.emit('cat:ragdoll', { reason, impulse: clone(impulse) });
  }

  /**
   * SOFT FAIL. Granny scruffs the cat and plops it in its bed. Keys, objectives
   * and score are all KEPT; only heat resets and mischief-in-progress is undone.
   * Failure is a funny beat and a speed bump, not a punishment - a 7-year-old
   * losing fifteen minutes of progress is a controller-on-the-floor moment.
   */
  private stepCatchSequence(dt: number): void {
    this.catchTimer += dt;
    const carry = tuning.granny.carry;

    // A beat on the GOTCHA! first, then she walks the cat home. She used to just
    // stand on top of it for 1.6 seconds while it teleported, which read as the
    // game glitching rather than as being caught.
    if (this.catchTimer >= carry.pauseSeconds) {
      this.granny.step(dt);
      // Her collider only actually moves when the world steps, and without this
      // she recomputes the same blocked move against a stale position forever.
      this.physics.step();
    }
    this.cat.holdAt(this.granny.carryPoint, this.granny.yaw);
    if (!this.granny.carryFinished) return;

    const bed = this.level.def.catBed;
    this.cat.teleport(vec3(bed[0], bed[1], bed[2]));
    this.cat.unlock();

    resetHeatAfterCatch(this.heat);
    clearTraces(this.traces);
    this.granny.resetAfterCatch();
    this.witnessedKnocks.clear();
    this.hideSpotId = null;
    this.hideEntryWitnessed = false;

    if (this.carriedProp) {
      this.props.drop(this.carriedProp, clone(this.cat.position));
      this.carriedProp = null;
    }

    this.phase = 'playing';
    this.bus.emit('cat:recovered', {});
  }

  /**
   * Everything the level card and the save file need.
   *
   * Assembled here rather than in the UI because it is gameplay truth - how many
   * breakables the level HAS is a level-data question, and the card must never
   * be in a position to answer it differently from the simulation.
   */
  get result(): LevelResult {
    const breakables = this.props.props.filter((prop) => prop.breakSpeed > 0);
    return {
      levelId: this.level.def.id,
      chaos: this.chaosScore,
      cozy: this.cozyPoints,
      keysCollected: this.keysCollected,
      keysRequired: this.level.def.keysRequired ?? 2,
      keysTotal: this.level.keys.length,
      detections: this.detections,
      breakablesBroken: breakables.filter((prop) => prop.broken).length,
      breakablesTotal: breakables.length,
      momentsFound: this.level.catMoments.filter((moment) => moment.completed).length,
      momentsTotal: this.level.catMoments.length,
      elapsedSeconds: this.elapsedSeconds,
    };
  }

  /** Scored once when the level is won, then frozen. Null while playing. */
  get scored(): ScoredResult | null {
    return this.finalScore;
  }

  private checkWin(): void {
    if (this.phase !== 'playing') return;
    const required = this.level.def.keysRequired ?? 2;
    if (this.keysCollected < required) return;
    if (!this.objectives.every((objective) => objective.complete)) return;

    this.phase = 'won';
    // Badge bonuses are applied here, once, through the score system. They used
    // to be a lone `if (detections === 0)` adding the Ghost bonus straight onto
    // chaosScore, so Hurricane and Curious Cat were defined in tuning, promised
    // in DESIGN.md, and paid out by nothing.
    this.finalScore = scoreLevel(this.result);
    this.bus.emit('level:won', { levelId: this.level.def.id });
    this.bus.emit('vfx:pop', {
      position: clone(this.cat.position),
      text: 'PURRFECT!',
      color: '#f2c14e',
    });
  }

  dispose(): void {
    this.granny.dispose();
    this.props.clear();
    clearTraces(this.traces);
    this.bus.clear();
    this.physics.free();
  }

  /** Compact snapshot for tests and the debug HUD. */
  get snapshot() {
    return {
      phase: this.phase,
      heat: this.heat.heat,
      tier: this.heat.tier,
      contact: this.heat.contact,
      grannyState: this.granny.machine.current,
      keysCollected: this.keysCollected,
      chaos: this.chaosScore,
      cozy: this.cozyPoints,
      detections: this.detections,
      hiding: this.hideSpotId,
    };
  }
}
